import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config';
import CampForm from './CampForm';
import CampReportModal from './CampReportModal';
import CampAttendanceModal from './CampAttendanceModal';
import './CampManagement.css';

interface Camp {
  id: string;
  name: string;
  camp_type: string;
  tier: string;
  start_date: string;
  end_date: string;
  status: string;
  capacity: number;
  enrolled_count: number;
  pricing_model: string;
  base_price?: number;
  weekly_price?: number;
  drop_in_price?: number;
  total_weeks: number;
  camp_start_time?: string;
  camp_end_time?: string;
  early_dropoff_enabled?: boolean;
  early_dropoff_fee?: number;
  late_pickup_enabled?: boolean;
  late_pickup_fee?: number;
  location_name?: string;
  theme?: string;
  description?: string;
  stripe_mode?: string;
  camp_days?: Array<{
    id: string;
    day_number: number;
    week_number: number;
    date: string;
    is_available: boolean;
    unavailable_reason?: string;
  }>;
}

interface CampEnrollment {
  id: string;
  student_name: string;
  parent_name: string;
  enrollment_type: string;
  selected_weeks?: number[];
  days_attending?: string[];
  enrollment_date: string;
  base_price: number;
  discount_amount: number;
  final_price: number;
  payment_status: string;
  needs_early_dropoff: boolean;
  needs_late_pickup: boolean;
  dietary_restrictions?: string;
  allergies?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
}

const CampManagement: React.FC = () => {
  const { user } = useAuth();
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showDetailView, setShowDetailView] = useState(false);
  const [viewingCamp, setViewingCamp] = useState<Camp | null>(null);
  const [campEnrollments, setCampEnrollments] = useState<CampEnrollment[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [showCampReport, setShowCampReport] = useState(false);
  const [showCampAttendance, setShowCampAttendance] = useState(false);

  useEffect(() => {
    fetchCamps();
  }, [filterStatus, filterType]);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const params = new URLSearchParams();
      params.append('include_days', 'true'); // Include day details for better card display
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('camp_type', filterType);

      const response = await fetch(`${config.BACKEND_URL}/api/camps?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCamps(data);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampEnrollments = async (campId: string) => {
    try {
      setLoadingEnrollments(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}/enrollments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCampEnrollments(data);
      } else {
        console.error('Failed to fetch enrollments');
        setCampEnrollments([]);
      }
    } catch (error) {
      console.error('Error fetching camp enrollments:', error);
      setCampEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleViewDetails = (camp: Camp) => {
    setViewingCamp(camp);
    setShowDetailView(true);
    fetchCampEnrollments(camp.id);
  };

  const handleCreateNew = () => {
    setSelectedCamp(null);
    setShowForm(true);
  };

  const handleEdit = (camp: Camp) => {
    setSelectedCamp(camp);
    setShowForm(true);
  };

  const handleDelete = async (campId: string) => {
    if (!window.confirm('Are you sure you want to delete this camp? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Camp deleted successfully');
        fetchCamps();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to delete camp'}`);
      }
    } catch (error) {
      console.error('Error deleting camp:', error);
      alert('Error deleting camp');
    }
  };

  const handlePublish = async (campId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        alert(`Camp status changed to ${newStatus}`);
        fetchCamps();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update status'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating camp status');
    }
  };

  const handleToggleStripeMode = async (campId: string, currentMode: string) => {
    const newMode = currentMode === 'live' ? 'test' : 'live';
    
    // Confirmation for switching to LIVE mode
    if (newMode === 'live') {
      const confirmed = window.confirm(
        '⚠️ WARNING: Switching to LIVE mode will process REAL payments!\n\n' +
        'Real money will be charged to parents who enroll in this camp.\n\n' +
        'Are you sure you want to enable LIVE mode?'
      );
      if (!confirmed) return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stripe_mode: newMode })
      });

      if (response.ok) {
        alert(`✅ Stripe mode changed to ${newMode.toUpperCase()}\n\n` +
              (newMode === 'live' 
                ? '⚠️ This camp will now process REAL payments!' 
                : '✅ This camp will now use test payments (safe).'));
        fetchCamps();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update Stripe mode'}`);
      }
    } catch (error) {
      console.error('Error updating Stripe mode:', error);
      alert('Error updating Stripe mode');
    }
  };

  const formatDate = (dateStr: string) => {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'published': return 'badge-success';
      case 'draft': return 'badge-warning';
      case 'active': return 'badge-info';
      case 'completed': return 'badge-secondary';
      case 'cancelled': return 'badge-danger';
      case 'full': return 'badge-purple';
      default: return 'badge-default';
    }
  };

  const getCampTypeLabel = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (showForm) {
    return (
      <CampForm
        camp={selectedCamp}
        onClose={() => {
          setShowForm(false);
          setSelectedCamp(null);
        }}
        onSave={() => {
          setShowForm(false);
          setSelectedCamp(null);
          fetchCamps();
        }}
      />
    );
  }

  return (
    <div className="camp-management">
      <div className="camp-header">
        <div>
          <h2>🏕️ Camp Management</h2>
          <p className="subtitle">Create and manage vacation camps, summer camps, and special programs</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => setShowCampReport(true)}>
            <span>📊</span> Camp Report
          </button>
          <button className="btn btn-secondary" onClick={() => setShowCampAttendance(true)}>
            <span>📋</span> Camp Attendance
          </button>
          <button className="btn btn-primary" onClick={handleCreateNew}>
            <span>➕</span> Create New Camp
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="camp-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="full">Full</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="winter_camp">Winter Camp</option>
            <option value="summer_camp">Summer Camp</option>
            <option value="spring_break_camp">Spring Break Camp</option>
            <option value="pro_d_day_camp">Pro-D Day Camp</option>
            <option value="holiday_camp">Holiday Camp</option>
            <option value="specialty_camp">Specialty Camp</option>
          </select>
        </div>

        <div className="filter-stats">
          <span className="stat-item">
            <strong>{camps.length}</strong> Total Camps
          </span>
          <span className="stat-item">
            <strong>{camps.filter(c => c.status === 'published').length}</strong> Published
          </span>
          <span className="stat-item">
            <strong>{camps.reduce((sum, c) => sum + c.enrolled_count, 0)}</strong> Total Enrollments
          </span>
        </div>
      </div>

      {/* Camps List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading camps...</p>
        </div>
      ) : camps.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏕️</div>
          <h3>No Camps Found</h3>
          <p>Create your first camp to get started!</p>
          <button className="btn btn-primary" onClick={handleCreateNew}>
            Create New Camp
          </button>
        </div>
      ) : (
        <div className="camps-grid">
          {camps.map(camp => (
            <div key={camp.id} className="camp-card">
              <div className="camp-card-header">
                <div>
                  <h3>{camp.name}</h3>
                  <p className="camp-theme">{camp.theme || camp.description}</p>
                  {((camp as any).age_min || (camp as any).age_max) && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: 'rgba(255, 255, 255, 0.95)', 
                      marginTop: '5px',
                      fontWeight: '600'
                    }}>
                      👦 Ages {(camp as any).age_min}-{(camp as any).age_max} | 
                      {camp.tier === 'foundations' ? ' 🌱 Elementary School' : 
                       camp.tier === 'innovators' ? ' 🚀 Middle School' : 
                       ' 🌟 High School'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className={`badge ${getStatusBadgeClass(camp.status)}`}>
                    {camp.status}
                  </span>
                  <span 
                    className="badge" 
                    style={{ 
                      backgroundColor: camp.stripe_mode === 'live' ? '#dc3545' : '#28a745',
                      color: 'white',
                      fontSize: '11px',
                      padding: '4px 8px',
                      fontWeight: 'bold'
                    }}
                    title={camp.stripe_mode === 'live' ? 'Using LIVE Stripe keys - Real payments' : 'Using TEST Stripe keys - Test payments only'}
                  >
                    💳 {camp.stripe_mode === 'live' ? 'LIVE' : 'TEST'}
                  </span>
                </div>
              </div>

              <div className="camp-card-body">
                <div className="camp-info-row">
                  <span className="info-label">📅 Dates:</span>
                  <span>{formatDate(camp.start_date)} - {formatDate(camp.end_date)}</span>
                </div>

                <div className="camp-info-row">
                  <span className="info-label">🎯 Type:</span>
                  <span>{getCampTypeLabel(camp.camp_type)}</span>
                </div>

                <div className="camp-info-row">
                  <span className="info-label">👥 Tier:</span>
                  <span className="tier-badge">{camp.tier}</span>
                </div>

                {camp.camp_start_time && (
                  <div className="camp-info-row">
                    <span className="info-label">⏰ Schedule:</span>
                    <span>{camp.camp_start_time} - {camp.camp_end_time}</span>
                  </div>
                )}

                {camp.location_name && (
                  <div className="camp-info-row">
                    <span className="info-label">📍 Location:</span>
                    <span>{camp.location_name}</span>
                  </div>
                )}

                <div className="camp-info-row">
                  <span className="info-label">💰 Pricing:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {camp.pricing_model === 'full_camp' && camp.base_price && (
                      <span><strong>Full Camp:</strong> ${Number(camp.base_price).toFixed(2)}</span>
                    )}
                    {(camp.pricing_model === 'weekly' || camp.pricing_model === 'weekly_and_drop_in') && camp.weekly_price && (
                      <span><strong>Weekly:</strong> ${Number(camp.weekly_price).toFixed(2)} × {camp.total_weeks} week{camp.total_weeks > 1 ? 's' : ''}</span>
                    )}
                    {(camp.pricing_model === 'drop_in' || camp.pricing_model === 'weekly_and_drop_in') && camp.drop_in_price && (
                      <span><strong>Drop-in:</strong> ${Number(camp.drop_in_price).toFixed(2)}/day</span>
                    )}
                    {camp.early_dropoff_enabled && camp.early_dropoff_fee && (
                      <span style={{ fontSize: '12px', color: '#666' }}>+ ${Number(camp.early_dropoff_fee).toFixed(2)} early drop-off</span>
                    )}
                    {camp.late_pickup_enabled && camp.late_pickup_fee && (
                      <span style={{ fontSize: '12px', color: '#666' }}>+ ${Number(camp.late_pickup_fee).toFixed(2)} late pickup</span>
                    )}
                  </div>
                </div>

                {camp.camp_days && camp.camp_days.length > 0 && (
                  <div className="camp-info-row">
                    <span className="info-label">📅 Days:</span>
                    <span>
                      {camp.camp_days.filter((d: any) => d.is_available).length} available days
                      {camp.camp_days.filter((d: any) => !d.is_available).length > 0 && 
                        ` (${camp.camp_days.filter((d: any) => !d.is_available).length} holidays)`}
                    </span>
                  </div>
                )}

                <div className="camp-capacity">
                  <div className="capacity-bar">
                    <div 
                      className="capacity-fill" 
                      style={{ width: `${(camp.enrolled_count / camp.capacity) * 100}%` }}
                    ></div>
                  </div>
                  <span className="capacity-text">
                    {camp.enrolled_count}/{camp.capacity} enrolled
                  </span>
                </div>
              </div>

              <div className="camp-card-footer">
                <button 
                  className="btn btn-sm btn-primary" 
                  onClick={() => handleViewDetails(camp)}
                >
                  👁️ View Details
                </button>

                <button 
                  className="btn btn-sm btn-secondary" 
                  onClick={() => handleEdit(camp)}
                >
                  ✏️ Edit
                </button>

                <button 
                  className="btn btn-sm" 
                  style={{
                    backgroundColor: camp.stripe_mode === 'live' ? '#28a745' : '#ffc107',
                    color: camp.stripe_mode === 'live' ? 'white' : '#000',
                    border: 'none',
                    fontWeight: 'bold'
                  }}
                  onClick={() => handleToggleStripeMode(camp.id, camp.stripe_mode || 'test')}
                  title={`Switch to ${camp.stripe_mode === 'live' ? 'TEST' : 'LIVE'} mode`}
                >
                  💳 {camp.stripe_mode === 'live' ? '→ TEST' : '→ LIVE'}
                </button>

                {camp.status === 'draft' && (
                  <button 
                    className="btn btn-sm btn-success" 
                    onClick={() => handlePublish(camp.id, 'published')}
                  >
                    📤 Publish
                  </button>
                )}

                {camp.status === 'published' && (
                  <button 
                    className="btn btn-sm btn-warning" 
                    onClick={() => handlePublish(camp.id, 'draft')}
                  >
                    📥 Unpublish
                  </button>
                )}

                <button 
                  className="btn btn-sm btn-danger" 
                  onClick={() => handleDelete(camp.id)}
                  disabled={camp.enrolled_count > 0}
                  title={camp.enrolled_count > 0 ? 'Cannot delete camp with enrollments' : 'Delete camp'}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Camp Detail View Modal */}
      {showDetailView && viewingCamp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '1200px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '25px',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px' }}>{viewingCamp.name}</h2>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>{viewingCamp.theme || viewingCamp.description}</p>
                {((viewingCamp as any).age_min || (viewingCamp as any).age_max) && (
                  <p style={{ 
                    margin: '8px 0 0 0', 
                    fontSize: '13px', 
                    opacity: 0.95,
                    fontWeight: '600',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px'
                  }}>
                    👦 Ages {(viewingCamp as any).age_min}-{(viewingCamp as any).age_max} | 
                    {viewingCamp.tier === 'foundations' ? ' Elementary School' : 
                     viewingCamp.tier === 'innovators' ? ' Middle School' : 
                     ' High School'}
                  </p>
                )}
              </div>
              <button 
                onClick={() => {
                  setShowDetailView(false);
                  setViewingCamp(null);
                  setCampEnrollments([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px 10px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '30px' }}>
              {/* Camp Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>📋 Camp Information</h3>
                
                {/* Description */}
                {(viewingCamp as any).description && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#f0f4ff', 
                    borderLeft: '4px solid #667eea',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <p style={{ margin: 0, whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                      {(viewingCamp as any).description}
                    </p>
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  <div>
                    <p><strong>📅 Dates:</strong> {formatDate(viewingCamp.start_date)} - {formatDate(viewingCamp.end_date)}</p>
                    <p><strong>⏰ Core Hours:</strong> {viewingCamp.camp_start_time} - {viewingCamp.camp_end_time} PST</p>
                    {(viewingCamp as any).age_min && (viewingCamp as any).age_max && (
                      <p><strong>👦 Ages:</strong> {(viewingCamp as any).age_min}-{(viewingCamp as any).age_max} years</p>
                    )}
                    <p><strong>🎯 Type:</strong> {getCampTypeLabel(viewingCamp.camp_type)}</p>
                    <p><strong>👥 Tier:</strong> {viewingCamp.tier.toUpperCase()}</p>
                    {(viewingCamp as any).theme && (
                      <p><strong>🎨 Theme:</strong> {(viewingCamp as any).theme}</p>
                    )}
                  </div>
                  
                  <div>
                    <p><strong>📍 Location:</strong> {viewingCamp.location_name || 'TBD'}</p>
                    {(viewingCamp as any).location_address && (
                      <p style={{ fontSize: '13px', color: '#666', marginLeft: '20px' }}>
                        {(viewingCamp as any).location_address}
                      </p>
                    )}
                    <p><strong>👨‍👩‍👧‍👦 Capacity:</strong> {viewingCamp.capacity}</p>
                    <p><strong>✅ Enrolled:</strong> {viewingCamp.enrolled_count}</p>
                    <p><strong>🎫 Available:</strong> {viewingCamp.capacity - viewingCamp.enrolled_count}</p>
                    <p><strong>📊 Status:</strong> <span style={{ 
                      textTransform: 'uppercase', 
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: viewingCamp.status === 'published' ? '#d4edda' : 
                             viewingCamp.status === 'draft' ? '#e2e3e5' : 
                             viewingCamp.status === 'active' ? '#cce5ff' : 
                             viewingCamp.status === 'full' ? '#f8d7da' : '#f8f9fa',
                      color: viewingCamp.status === 'published' ? '#155724' : 
                             viewingCamp.status === 'draft' ? '#383d41' : 
                             viewingCamp.status === 'active' ? '#004085' : 
                             viewingCamp.status === 'full' ? '#721c24' : '#333'
                    }}>{viewingCamp.status}</span></p>
                  </div>
                  
                  <div>
                    <p><strong>💰 Pricing Model:</strong> {viewingCamp.pricing_model.replace(/_/g, ' ').toUpperCase()}</p>
                    {viewingCamp.pricing_model === 'full_camp' && viewingCamp.base_price && (
                      <p><strong>Full Camp:</strong> ${Number(viewingCamp.base_price).toFixed(2)}</p>
                    )}
                    {(viewingCamp.pricing_model === 'weekly' || viewingCamp.pricing_model === 'weekly_and_drop_in') && viewingCamp.weekly_price && (
                      <p><strong>Weekly:</strong> ${Number(viewingCamp.weekly_price).toFixed(2)}/week</p>
                    )}
                    {(viewingCamp.pricing_model === 'drop_in' || viewingCamp.pricing_model === 'weekly_and_drop_in') && viewingCamp.drop_in_price && (
                      <p><strong>Drop-in:</strong> ${Number(viewingCamp.drop_in_price).toFixed(2)}/day</p>
                    )}
                    {viewingCamp.early_dropoff_enabled && viewingCamp.early_dropoff_fee && (
                      <p><strong>🌅 Early Drop-off:</strong> ${Number(viewingCamp.early_dropoff_fee).toFixed(2)}/day 
                        <span style={{ fontSize: '12px', color: '#666', display: 'block' }}>
                          ({(viewingCamp as any).early_dropoff_start} - {(viewingCamp as any).early_dropoff_end})
                        </span>
                      </p>
                    )}
                    {viewingCamp.late_pickup_enabled && viewingCamp.late_pickup_fee && (
                      <p><strong>🌙 Late Pickup:</strong> ${Number(viewingCamp.late_pickup_fee).toFixed(2)}/day
                        <span style={{ fontSize: '12px', color: '#666', display: 'block' }}>
                          ({(viewingCamp as any).late_pickup_start} - {(viewingCamp as any).late_pickup_end})
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Materials Section */}
                {((viewingCamp as any).materials_provided || (viewingCamp as any).materials_students_bring) && (
                  <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {(viewingCamp as any).materials_provided && (
                      <div style={{ padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>✅ Materials Provided</h4>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {(viewingCamp as any).materials_provided.map((item: string, idx: number) => (
                            <li key={idx} style={{ fontSize: '14px', marginBottom: '5px' }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(viewingCamp as any).materials_students_bring && (
                      <div style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#e65100' }}>🎒 Students Should Bring</h4>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {(viewingCamp as any).materials_students_bring.map((item: string, idx: number) => (
                            <li key={idx} style={{ fontSize: '14px', marginBottom: '5px' }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Camp Days Schedule with Activities */}
                {viewingCamp.camp_days && viewingCamp.camp_days.length > 0 && (
                  <div style={{ marginTop: '30px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#667eea', fontSize: '18px' }}>📅 Camp Days Schedule</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                      {viewingCamp.camp_days.map(day => {
                        const [year, month, dayNum] = day.date.split('-').map(Number);
                        const date = new Date(year, month - 1, dayNum);
                        const dayData = day as any;
                        
                        return (
                          <div key={day.id} style={{
                            padding: '15px',
                            backgroundColor: day.is_available ? '#fff' : '#fee',
                            border: `2px solid ${day.is_available ? '#667eea' : '#f5c6cb'}`,
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}>
                            {/* Day Header */}
                            <div style={{ 
                              borderBottom: '2px solid #667eea', 
                              paddingBottom: '10px', 
                              marginBottom: '10px' 
                            }}>
                              <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#667eea' }}>
                                Day {day.day_number} - {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </div>
                              {dayData.theme && (
                                <div style={{ fontSize: '14px', color: '#333', marginTop: '5px', fontWeight: '600' }}>
                                  {dayData.theme}
                                </div>
                              )}
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>Week {day.week_number}</div>
                            </div>
                            
                            {/* Description */}
                            {dayData.description && (
                              <p style={{ fontSize: '13px', color: '#555', marginBottom: '10px', lineHeight: '1.5' }}>
                                {dayData.description}
                              </p>
                            )}
                            
                            {/* Activities Schedule */}
                            {dayData.activities && Array.isArray(dayData.activities) && dayData.activities.length > 0 && (
                              <div style={{ marginTop: '10px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#667eea', marginBottom: '5px' }}>
                                  Daily Schedule:
                                </div>
                                <div style={{ fontSize: '12px' }}>
                                  {dayData.activities.map((activity: any, idx: number) => (
                                    <div key={idx} style={{ 
                                      padding: '5px', 
                                      marginBottom: '3px',
                                      backgroundColor: activity.type === 'break' ? '#fff9e6' : 
                                                      activity.type === 'introduction' ? '#e3f2fd' :
                                                      activity.focus ? '#f3e5f5' : '#f8f9fa',
                                      borderRadius: '3px',
                                      display: 'flex',
                                      gap: '8px'
                                    }}>
                                      <span style={{ fontWeight: 'bold', color: '#667eea', minWidth: '90px' }}>
                                        {activity.time}
                                      </span>
                                      <span style={{ flex: 1 }}>
                                        {activity.activity}
                                        {activity.focus && (
                                          <span style={{ fontSize: '10px', color: '#666', marginLeft: '5px' }}>
                                            ({activity.focus})
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Special Notes */}
                            {dayData.special_notes && (
                              <div style={{ 
                                marginTop: '10px', 
                                fontSize: '12px', 
                                padding: '8px', 
                                backgroundColor: '#fff3cd', 
                                borderRadius: '4px',
                                color: '#856404'
                              }}>
                                <strong>📌 Note:</strong> {dayData.special_notes}
                              </div>
                            )}
                            
                            {!day.is_available && (
                              <div style={{ 
                                marginTop: '10px',
                                padding: '8px',
                                backgroundColor: '#f8d7da',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#721c24',
                                fontWeight: 'bold'
                              }}>
                                ❌ {day.unavailable_reason || 'Day Unavailable'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Enrollments List */}
              <div>
                <h3 style={{ marginBottom: '20px', borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>
                  Enrollments ({campEnrollments.length})
                </h3>

                {loadingEnrollments ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <p>Loading enrollments...</p>
                  </div>
                ) : campEnrollments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <p style={{ color: '#666' }}>No enrollments yet</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px',
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Student</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Parent</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Type</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Selection</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Enrolled</th>
                          <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Price</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Payment</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Extended</th>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Health/Contact</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campEnrollments.map((enrollment, index) => (
                          <tr key={enrollment.id} style={{
                            backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                            borderBottom: '1px solid #dee2e6'
                          }}>
                            <td style={{ padding: '12px', verticalAlign: 'top' }}>
                              <div style={{ fontWeight: 'bold', color: '#667eea' }}>{enrollment.student_name}</div>
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top' }}>
                              <div style={{ fontSize: '13px', color: '#666' }}>{enrollment.parent_name}</div>
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top' }}>
                              <span style={{
                                padding: '4px 8px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: '#1976d2'
                              }}>
                                {enrollment.enrollment_type.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', fontSize: '13px' }}>
                              {enrollment.enrollment_type === 'weekly' && enrollment.selected_weeks && (
                                <div>Week {enrollment.selected_weeks.join(', ')}</div>
                              )}
                              {enrollment.enrollment_type === 'drop_in' && enrollment.days_attending && (
                                <div>{enrollment.days_attending.length} day(s)</div>
                              )}
                              {enrollment.enrollment_type === 'full_camp' && (
                                <div>Full camp</div>
                              )}
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', fontSize: '13px' }}>
                              {(() => {
                                const [year, month, day] = enrollment.enrollment_date.split('T')[0].split('-').map(Number);
                                const date = new Date(year, month - 1, day);
                                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              })()}
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', textAlign: 'right' }}>
                              <div style={{ fontSize: '13px', color: '#666' }}>${Number(enrollment.base_price).toFixed(2)}</div>
                              {enrollment.discount_amount > 0 && (
                                <div style={{ fontSize: '12px', color: '#28a745' }}>-${Number(enrollment.discount_amount).toFixed(2)}</div>
                              )}
                              <div style={{ fontWeight: 'bold', color: '#667eea', marginTop: '4px' }}>
                                ${Number(enrollment.final_price).toFixed(2)}
                              </div>
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', textAlign: 'center' }}>
                              <span className={`badge ${
                                enrollment.payment_status === 'paid' ? 'badge-success' : 
                                enrollment.payment_status === 'pending' ? 'badge-warning' : 
                                'badge-secondary'
                              }`} style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}>
                                {enrollment.payment_status.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', textAlign: 'center', fontSize: '12px' }}>
                              {enrollment.needs_early_dropoff && <div>✓ Early</div>}
                              {enrollment.needs_late_pickup && <div>✓ Late</div>}
                              {!enrollment.needs_early_dropoff && !enrollment.needs_late_pickup && <div style={{ color: '#999' }}>—</div>}
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', fontSize: '12px' }}>
                              {enrollment.dietary_restrictions && (
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Diet:</strong> {enrollment.dietary_restrictions}
                                </div>
                              )}
                              {enrollment.allergies && (
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Allergies:</strong> {enrollment.allergies}
                                </div>
                              )}
                              {enrollment.emergency_contact_name && (
                                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #dee2e6' }}>
                                  <strong>Emergency:</strong><br />
                                  {enrollment.emergency_contact_name}<br />
                                  {enrollment.emergency_contact_phone}
                                </div>
                              )}
                              {!enrollment.dietary_restrictions && !enrollment.allergies && !enrollment.emergency_contact_name && (
                                <div style={{ color: '#999' }}>—</div>
                              )}
                            </td>
                            <td style={{ padding: '12px', verticalAlign: 'top', textAlign: 'center' }}>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={async () => {
                                  if (!window.confirm(`Are you sure you want to unenroll ${enrollment.student_name} from this camp?`)) {
                                    return;
                                  }
                                  
                                  try {
                                    // Use token refresh utility to get a valid token
                                    const { getValidToken } = await import('../../utils/tokenRefresh');
                                    const token = await getValidToken();
                                    
                                    if (!token) {
                                      alert('❌ Authentication required. Please log in again.');
                                      return;
                                    }
                                    
                                    const response = await fetch(
                                      `${config.BACKEND_URL}/api/camps/enrollments/${enrollment.id}/cancel`,
                                      {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        }
                                      }
                                    );
                                    
                                    if (response.ok) {
                                      alert(`✅ ${enrollment.student_name} has been unenrolled from the camp.`);
                                      // Refresh enrollments and camps list
                                      if (viewingCamp) {
                                        fetchCampEnrollments(viewingCamp.id);
                                      }
                                      fetchCamps(); // Refresh camp capacity
                                    } else {
                                      const errorData = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
                                      alert(`❌ Failed to unenroll: ${errorData.detail || 'Unknown error'}`);
                                    }
                                  } catch (error) {
                                    console.error('Error unenrolling student:', error);
                                    alert('❌ An error occurred while unenrolling the student.');
                                  }
                                }}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                🚫 Unenroll
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '20px 30px',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDetailView(false);
                  setViewingCamp(null);
                  setCampEnrollments([]);
                }}
                style={{ padding: '10px 20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCampReport && (
        <CampReportModal onClose={() => setShowCampReport(false)} />
      )}
      {showCampAttendance && (
        <CampAttendanceModal onClose={() => setShowCampAttendance(false)} />
      )}
    </div>
  );
};

export default CampManagement;

